import { Optional } from "@/types/util"
import { Honeybadger } from "@honeybadger-io/react"

import { CollectionTypes, exists, present, some } from "./presence"

// Used with TypeScript narrowing for exhaustive checks. If it's
// ok to ignore the case that's not covered, use warnUnreachable().
export function assertUnreachable(value: never): never {
  throw new Error(
    `reached code that was expected to be unreachable, with value ${JSON.stringify(value)}`
  )
}

// Like assertUnreachable(), but posts a Honeybadger notification
// instead of throwing an error. For use when it's ok to ignore
// the case that's not covered.
export function warnUnreachable(value: never): void {
  Honeybadger.notify(
    `reached code that was expected to be unreachable with value ${JSON.stringify(value)}`
  )
}

// This assertion also works as a TypeScript type guard, so you can
// use it for narrowing: after using it, the compiler knows that
// "condition" is true.
export function assert(
  condition: unknown,
  description: string
): asserts condition {
  if (!condition) {
    throw new Error(`assertion failed: ${description}`)
  }
}

export function assertExists<T>(value: Optional<T>, message?: string): T {
  if (!exists(value)) {
    throw new Error(message || "expected value to exist")
  }
  return value
}

export function assertPresent<T>(value: Optional<T>, message?: string): T {
  if (!present(value)) {
    throw new Error(message || "expected value to be present")
  }
  return value
}

export function assertSome<T extends CollectionTypes>(
  value: Optional<T>,
  message?: string
): T {
  if (!some(value)) {
    throw new Error(message || "expected value to not be empty")
  }
  return value
}

export function isNumber(value: unknown): value is number {
  return typeof value === "number" && !Number.isNaN(value)
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

export function parseNumericString(value: string): number | undefined {
  const parsed = parseFloat(value)
  return isNumber(parsed) ? parsed : undefined
}
